import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Comment, { COMMENT_REACTION_EMOJIS } from '../../models/Comment';
import User from '../../models/User';
import { Icon } from '../Icon';

type CommentReactionsProps = {
    comment: Comment;
    user?: User | null;
    onToggle: (emoji: string) => void | Promise<unknown>;
    /** When false, hide the add-reaction control (e.g. own comments). */
    canReact?: boolean;
    disabled?: boolean;
};

function CommentReactions(props: CommentReactionsProps) {
    const { comment, user, onToggle, canReact = true, disabled } = props;
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!pickerOpen)
            return undefined;

        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node))
                setPickerOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [pickerOpen]);

    const counts = useMemo(() => {
        const map: Record<string, number> = {};
        (comment.reactions || []).forEach((reaction) => {
            map[reaction.emoji] = (map[reaction.emoji] || 0) + 1;
        });
        return map;
    }, [comment.reactions]);

    const visibleEmojis = COMMENT_REACTION_EMOJIS.filter((emoji) => (counts[emoji] || 0) > 0);

    if (!canReact && visibleEmojis.length === 0)
        return null;

    const handleToggle = async (emoji: string) => {
        if (!canReact || disabled || pending)
            return;
        setPending(true);
        setPickerOpen(false);
        try {
            await onToggle(emoji);
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="inline-flex flex-wrap items-center gap-1 relative leading-none" ref={rootRef}>
            {visibleEmojis.map((emoji) => {
                const reacted = comment.hasReacted(user?._id, emoji);
                const names = comment.reactionUsers(emoji);
                const chipClass = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs leading-none transition-colors ${
                    reacted
                        ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/40 text-blue-800'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                } ${canReact ? 'hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-900 cursor-pointer' : 'cursor-default'}`;

                if (!canReact) {
                    return (
                        <span
                            key={emoji}
                            title={names.join(', ')}
                            className={chipClass}
                        >
                            <span aria-hidden="true">{emoji}</span>
                            <span>{counts[emoji]}</span>
                        </span>
                    );
                }

                return (
                    <button
                        key={emoji}
                        type="button"
                        disabled={disabled || pending}
                        title={names.join(', ')}
                        onClick={() => handleToggle(emoji)}
                        className={chipClass}
                    >
                        <span aria-hidden="true">{emoji}</span>
                        <span>{counts[emoji]}</span>
                    </button>
                );
            })}

            {canReact && (
                <div className="relative inline-flex items-center">
                    <button
                        type="button"
                        disabled={disabled || pending}
                        title={t('changes.react_to_comment') || 'Add reaction'}
                        onClick={() => setPickerOpen((open) => !open)}
                        className="inline-flex items-center justify-center leading-none p-0 text-gray-500 hover:text-gray-800 dark:text-gray-100 bg-transparent border-0"
                    >
                        <Icon type="addReaction" size={5} className="text-gray-500 dark:text-gray-400" />
                    </button>
                    {pickerOpen && (
                        <div className="absolute left-0 top-full z-20 mt-1 flex gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1 shadow-sm">
                            {COMMENT_REACTION_EMOJIS.map((emoji) => (
                                <button
                                    key={emoji}
                                    type="button"
                                    disabled={disabled || pending}
                                    onClick={() => handleToggle(emoji)}
                                    className={`rounded px-1.5 py-0.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 ${
                                        comment.hasReacted(user?._id, emoji) ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                                    }`}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CommentReactions;
